import { useEffect, useState } from "react";
import { Show, useAuth } from "@clerk/react";
import { Redirect, useLocation } from "wouter";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Task #542 — Coach portal landing after Clerk sign-in via the
 * "Send portal invite" email. Mirrors the guest-accept pattern: when
 * signed-out we punt to /sign-in (preserving ?coachId=), when signed-in
 * we POST /api/portal/accept which binds the new Clerk userId to the
 * coach row and upserts a role=coach org_members mirror, then we drop
 * the user into the portal home.
 */
export default function CoachPortalAccept() {
  return (
    <>
      <Show when="signed-out">
        <Redirect
          to={`/sign-in?redirect_url=${encodeURIComponent(
            `/coach-portal/accept${typeof window !== "undefined" ? window.location.search : ""}`,
          )}`}
        />
      </Show>
      <Show when="signed-in">
        <CoachPortalAcceptInner />
      </Show>
    </>
  );
}

function CoachPortalAcceptInner() {
  const { isLoaded, getToken } = useAuth();
  const [, setLocation] = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const params = new URLSearchParams(window.location.search);
        const cid = params.get("coachId");
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch("/api/portal/accept", {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({ coachId: cid ? Number(cid) : undefined }),
        });
        if (cancelled) return;
        if (res.status === 401) {
          setError("Your sign-in session has expired. Please sign in again.");
          return;
        }
        if (res.status === 404) {
          setError(
            "We couldn't find a coach profile waiting for this email. Ask the admin who invited you to re-send the invite.",
          );
          return;
        }
        if (!res.ok) {
          setError("Something went wrong. Please try again or contact the admin who invited you.");
          return;
        }
        setLocation("/portal");
      } catch {
        if (!cancelled) setError("Network error. Please retry.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, getToken, setLocation]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-4">
        {error ? (
          <>
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
            <h1 className="text-lg font-semibold">Portal access could not be activated</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={() => setLocation("/")}>Return home</Button>
          </>
        ) : (
          <>
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
            <h1 className="text-lg font-semibold">Activating your coach portal…</h1>
            <p className="text-sm text-muted-foreground">
              We're matching your invitation. This only takes a second.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
